/**
 * Read-only view-model for a per-layer reservoir development passport.
 * Assembles MB config + last_result + wells + layer resource — no invented defaults.
 */
import type { ScenarioLayer } from '@/api/scenarioLayers';
import type { ScenarioWell } from '@/types/scenarioWell';
import type {
  MaterialBalanceConfigResponse,
  MaterialBalanceFieldPoint,
  MaterialBalanceResult,
  MbGridYear,
  MbPoint,
  MbPropsOverrides,
} from '@/types/materialBalance';
import { MB_PROP_ROWS } from '@/utils/materialBalanceProps';
import {
  coreyRelPermCurve,
  inferRelPermEndpoints,
  maskZeroKrForChart,
  relPermPtsFromTable,
  type RelPermChartPoint,
} from '@/utils/materialBalanceRelperm';

export type PassportWellKind = 'prod' | 'inj';

export type PassportWellRow = {
  id: string;
  well_name: string;
  kind: PassportWellKind;
  t0: number | null;
  t1: number | null;
  x: number | null;
  y: number | null;
  q_start_td: number | null;
};

export type PassportKv = {
  label: string;
  value: string;
  unit?: string;
};

export type LayerPassportViewModel = {
  layerId: string;
  layerName: string;
  status: string;
  hasResult: boolean;
  /** Accordion summary chips */
  headerKpis: PassportKv[];
  summary: PassportKv[];
  propsRows: PassportKv[];
  diagnostics: PassportKv[];
  wells: PassportWellRow[];
  nProd: number;
  nInj: number;
  scheduleSpan: string;
  injectionNote: string;
  relpermMode: string;
  relpermSummary: PassportKv[];
  relpermChart: RelPermChartPoint[];
  forecast: MaterialBalanceFieldPoint[];
  forecastKpis: PassportKv[];
  extractableOilKt: number | null;
  xDomain: [number, number] | null;
  contour: MbPoint[];
  contourOrigin: { lat: number; lng: number } | null;
  mapWells: Array<{
    id: string;
    x: number;
    y: number;
    kind: PassportWellKind;
    t0?: number;
  }>;
  lastGrid: MbGridYear | null;
  so0: number;
};

const DASH = '—';

function fmtNum(v: number | null | undefined, digits = 2): string {
  if (v == null || !Number.isFinite(v)) return DASH;
  const abs = Math.abs(v);
  if (abs >= 100 || Number.isInteger(v)) return String(Math.round(v));
  if (abs >= 10) return (Math.round(v * 10) / 10).toString();
  return (Math.round(v * 10 ** digits) / 10 ** digits).toString();
}

function fmtFrac(v: number | null | undefined): string {
  if (v == null || !Number.isFinite(v)) return DASH;
  return (Math.round(v * 1000) / 1000).toString();
}

function fmtPct(v: number | null | undefined): string {
  if (v == null || !Number.isFinite(v)) return DASH;
  return `${(Math.round(v * 10) / 10).toString()}%`;
}

function yearFromEntry(entryDate?: string | null): number | null {
  if (!entryDate || typeof entryDate !== 'string') return null;
  const y = Number(entryDate.slice(0, 4));
  return Number.isFinite(y) ? y : null;
}

export function isPassportInjectionWell(well: ScenarioWell): boolean {
  const typeName = (well.well_type?.name || well.well?.type || '').toLowerCase();
  if (typeName === 'injection' || typeName === 'нагнетательная') return true;
  if (typeName === 'production' || typeName === 'debit' || typeName === 'добывающая') return false;
  return /_inj(?:_|$)/i.test(well.well_name || well.well?.name || '');
}

function propValue(
  props: MbPropsOverrides | undefined,
  key: keyof MbPropsOverrides,
): number | null {
  if (!props) return null;
  const v = props[key];
  if (typeof v === 'number' && Number.isFinite(v)) return v;
  return null;
}

function extractableOil(
  ooip: number | null | undefined,
  rf: number | null | undefined,
): number | null {
  if (ooip == null || rf == null || !(ooip > 0) || !(rf > 0)) return null;
  return ooip * rf;
}

function countInjYears(
  injField: Record<string, number> | undefined,
  injWells: Record<string, Record<string, number>> | undefined,
): { fieldYears: number; wellKeys: number; multKeys: number } {
  const fieldYears = Object.keys(injField || {}).length;
  const wellKeys = Object.keys(injWells || {}).length;
  return { fieldYears, wellKeys, multKeys: 0 };
}

function injectionNote(
  config: MaterialBalanceConfigResponse['config'] | undefined,
): string {
  if (!config) return DASH;
  const { fieldYears, wellKeys } = countInjYears(config.inj_field, config.inj_wells);
  const multKeys = Object.keys(config.inj_mult || {}).length;
  const parts: string[] = [];
  if (fieldYears > 0) parts.push(`field schedule ${fieldYears} yr`);
  if (wellKeys > 0) parts.push(`per-well overrides ${wellKeys} inj`);
  if (multKeys > 0) parts.push(`multipliers ${multKeys}`);
  return parts.length ? parts.join(' · ') : 'No injection schedule';
}

function buildWellRows(wells: ScenarioWell[]): PassportWellRow[] {
  return wells
    .map((w) => {
      const kind: PassportWellKind = isPassportInjectionWell(w) ? 'inj' : 'prod';
      const t0 = yearFromEntry(w.entry_date);
      const t1 =
        w.end_year != null && Number.isFinite(w.end_year) ? Number(w.end_year) : null;
      const x = w.x_km != null && Number.isFinite(w.x_km) ? Number(w.x_km) : null;
      const y = w.y_km != null && Number.isFinite(w.y_km) ? Number(w.y_km) : null;
      const q =
        kind === 'prod' && w.ini_liquid != null && Number.isFinite(w.ini_liquid)
          ? Number(w.ini_liquid)
          : null;
      return {
        id: w.id || w.scenario_well_id,
        well_name: w.well_name || w.well?.name || w.id,
        kind,
        t0,
        t1,
        x,
        y,
        q_start_td: q,
      };
    })
    .sort((a, b) => {
      if (a.kind !== b.kind) return a.kind === 'prod' ? -1 : 1;
      return a.well_name.localeCompare(b.well_name);
    });
}

function forecastKpisFromField(field: MaterialBalanceFieldPoint[]): PassportKv[] {
  if (!field.length) return [];
  let peakOil = 0;
  let maxWells = 0;
  for (const r of field) {
    peakOil = Math.max(peakOil, r.oil || 0);
    maxWells = Math.max(maxWells, (r.n_prod || 0) + (r.n_inj || 0));
  }
  const last = field[field.length - 1];
  return [
    { label: 'Peak oil', value: fmtNum(peakOil, 1), unit: 'kt/yr' },
    { label: 'Np (end)', value: fmtNum(last.np, 1), unit: 'kt' },
    { label: 'Water cut (end)', value: fmtPct(last.fw) },
    {
      label: 'Recovery factor (end)',
      value: last.recovery_factor != null ? fmtFrac(last.recovery_factor) : DASH,
    },
    { label: 'Max active wells', value: fmtNum(maxWells, 0) },
  ];
}

function relpermBundle(mb: MaterialBalanceConfigResponse | null | undefined): {
  mode: string;
  summary: PassportKv[];
  chart: RelPermChartPoint[];
} {
  const config = mb?.config;
  const mode = (config?.relperm_mode || 'corey').toLowerCase();
  const props = config?.props || {};
  const summary: PassportKv[] = [{ label: 'Mode', value: mode === 'table' ? 'table' : 'Corey' }];

  if (mode === 'table') {
    const pts = relPermPtsFromTable(config?.kr_table);
    if (pts.length) {
      const { swi, sor } = inferRelPermEndpoints(pts);
      summary.push(
        { label: 'Swi', value: fmtFrac(swi) },
        { label: 'Sor', value: fmtFrac(sor) },
        { label: 'Table rows', value: String(pts.length) },
      );
      return { mode: 'table', summary, chart: maskZeroKrForChart(pts) };
    }
    summary.push({ label: 'Table', value: 'empty' });
    return { mode: 'table', summary, chart: [] };
  }

  const krw0 = propValue(props, 'krw0') ?? 0.35;
  const nw = propValue(props, 'nw') ?? 2;
  const no = propValue(props, 'no') ?? 3;
  const so = propValue(props, 'so');
  const kvyt = propValue(props, 'kvyt') ?? mb?.last_result?.kvyt_used ?? null;
  const { swi, sor, pts } = coreyRelPermCurve({
    so: so ?? 0.5,
    kvyt: kvyt ?? 0.4,
    krw0,
    nw,
    no,
  });
  summary.push(
    { label: 'krw0', value: fmtFrac(krw0) },
    { label: 'nw', value: fmtNum(nw, 2) },
    { label: 'no', value: fmtNum(no, 2) },
    { label: 'Swi', value: fmtFrac(swi) },
    { label: 'Sor', value: fmtFrac(sor) },
  );
  return { mode: 'corey', summary, chart: maskZeroKrForChart(pts) };
}

/**
 * Build a passport view-model for one scenario layer.
 * `mb` may be null if getMaterialBalance failed; wells filtered to this layer.
 */
export function buildLayerPassportViewModel(
  layer: ScenarioLayer,
  mb: MaterialBalanceConfigResponse | null | undefined,
  wells: ScenarioWell[],
): LayerPassportViewModel {
  const layerWells = wells.filter((w) => w.scenario_layer_id === layer.scenario_layer_id);
  const wellRows = buildWellRows(layerWells);
  const nProd = wellRows.filter((w) => w.kind === 'prod').length;
  const nInj = wellRows.filter((w) => w.kind === 'inj').length;

  const t0s = wellRows.map((w) => w.t0).filter((y): y is number => y != null);
  const t1s = wellRows.map((w) => w.t1).filter((y): y is number => y != null);
  const scheduleSpan =
    t0s.length || t1s.length
      ? `${t0s.length ? Math.min(...t0s) : DASH} – ${t1s.length ? Math.max(...t1s) : DASH}`
      : DASH;

  const result: MaterialBalanceResult | null | undefined = mb?.last_result;
  const hasResult = Boolean(result?.field?.length);
  const field = (result?.field || []) as MaterialBalanceFieldPoint[];
  const last = field.length ? field[field.length - 1] : null;

  const ooip =
    mb?.initial_geological_kt ??
    result?.initial_geological_kt ??
    propValue(mb?.config?.props, 'ngz_kt') ??
    layer.resource?.initial_geological ??
    null;

  const targetRf =
    mb?.target_recovery_factor ??
    result?.target_recovery_factor ??
    layer.recovery_factor ??
    null;

  const kvytUsed = result?.kvyt_used ?? propValue(mb?.config?.props, 'kvyt') ?? null;
  const extractable = extractableOil(ooip, kvytUsed ?? targetRf);

  const year0 = result?.year0 ?? (field[0]?.year ?? null);
  const year1 = result?.year1 ?? (last?.year ?? null);

  const headerKpis: PassportKv[] = [
    { label: 'Np', value: last ? fmtNum(last.np, 1) : DASH, unit: 'kt' },
    {
      label: 'RF',
      value: last?.recovery_factor != null ? fmtFrac(last.recovery_factor) : DASH,
    },
    { label: 'Prod / Inj', value: `${nProd} / ${nInj}` },
    {
      label: 'Extractable',
      value: extractable != null ? fmtNum(extractable, 1) : DASH,
      unit: 'kt',
    },
  ];

  const summary: PassportKv[] = [
    {
      label: 'Horizon',
      value: year0 != null && year1 != null ? `${year0} – ${year1}` : DASH,
    },
    { label: 'OOIP', value: fmtNum(ooip, 1), unit: 'kt' },
    { label: 'Target recovery factor', value: fmtFrac(targetRf) },
    {
      label: 'Extractable oil',
      value: extractable != null ? fmtNum(extractable, 1) : DASH,
      unit: 'kt',
    },
    { label: 'Np (end)', value: last ? fmtNum(last.np, 1) : DASH, unit: 'kt' },
    {
      label: 'Recovery factor (end)',
      value: last?.recovery_factor != null ? fmtFrac(last.recovery_factor) : DASH,
    },
    { label: 'Water cut (end)', value: last ? fmtPct(last.fw) : DASH },
    { label: 'Pressure (end)', value: last?.p != null ? fmtNum(last.p, 2) : DASH, unit: 'MPa' },
    {
      label: 'Cap by target RF',
      value:
        (result?.cap_by_target_rf ?? mb?.config?.cap_by_target_rf) != null
          ? (result?.cap_by_target_rf ?? mb?.config?.cap_by_target_rf)
            ? 'yes'
            : 'no'
          : DASH,
    },
    { label: 'Displacement efficiency (E_d)', value: fmtFrac(kvytUsed) },
  ];

  if (layer.recovery_factor != null && targetRf != null && Math.abs(layer.recovery_factor - targetRf) > 1e-6) {
    summary.push({ label: 'Layer recovery factor', value: fmtFrac(layer.recovery_factor) });
  }
  const resourceOoip = layer.resource?.initial_geological;
  if (
    resourceOoip != null &&
    ooip != null &&
    Math.abs(resourceOoip - ooip) > 1e-3
  ) {
    summary.push({ label: 'Resource OOIP', value: fmtNum(resourceOoip, 1), unit: 'kt' });
  }

  const props = mb?.config?.props || {};
  const propsRows: PassportKv[] = MB_PROP_ROWS.map((row) => {
    const raw = propValue(props, row.key as keyof MbPropsOverrides);
    return {
      label: row.label,
      value: raw != null ? (row.unit === 'frac' ? fmtFrac(raw) : fmtNum(raw, 3)) : DASH,
      unit: row.unit || undefined,
    };
  });

  const diagnostics: PassportKv[] = [];
  const resAny = result as (MaterialBalanceResult & Record<string, unknown>) | null | undefined;
  if (resAny) {
    for (const [key, label] of [
      ['area_km2', 'Area'],
      ['swi', 'Swi'],
      ['sor', 'Sor'],
      ['rf_el', 'RF elastic'],
      ['pvi_bt', 'PVI breakthrough'],
      ['sw_bt', 'Sw breakthrough'],
    ] as const) {
      const v = resAny[key];
      if (typeof v === 'number' && Number.isFinite(v)) {
        diagnostics.push({
          label,
          value: key === 'area_km2' ? fmtNum(v, 2) : fmtFrac(v),
          unit: key === 'area_km2' ? 'km²' : undefined,
        });
      }
    }
  }

  const rp = relpermBundle(mb);

  let xDomain: [number, number] | null = null;
  if (field.length) {
    const ys = field.map((r) => r.year);
    xDomain = [Math.min(...ys), Math.max(...ys)];
  } else if (year0 != null && year1 != null) {
    xDomain = [year0, year1];
  }

  const contour = mb?.config?.contour || [];
  const origin = mb?.config?.contour_origin;
  const contourOrigin =
    origin && Number.isFinite(origin.lat) && Number.isFinite(origin.lng)
      ? { lat: origin.lat, lng: origin.lng }
      : null;

  const mapWells = wellRows
    .filter((w) => w.x != null && w.y != null)
    .map((w) => ({
      id: w.well_name,
      x: w.x as number,
      y: w.y as number,
      kind: w.kind,
      t0: w.t0 ?? undefined,
    }));

  const grids = result?.grids || [];
  const lastGrid = grids.length ? grids[grids.length - 1] : null;
  const so0 = propValue(props, 'so') ?? 0.5;

  return {
    layerId: layer.scenario_layer_id,
    layerName: layer.layer_name || mb?.layer_name || 'Layer',
    status: mb?.status || (hasResult ? 'ready' : 'draft'),
    hasResult,
    headerKpis,
    summary,
    propsRows,
    diagnostics,
    wells: wellRows,
    nProd,
    nInj,
    scheduleSpan,
    injectionNote: injectionNote(mb?.config),
    relpermMode: rp.mode,
    relpermSummary: rp.summary,
    relpermChart: rp.chart,
    forecast: field,
    forecastKpis: forecastKpisFromField(field),
    extractableOilKt: extractable,
    xDomain,
    contour,
    contourOrigin,
    mapWells,
    lastGrid,
    so0,
  };
}
